<script setup>
  import moment from "moment";
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import { Loader2 } from "lucide-vue-next";
  import { Card, CardContent } from "@/components/ui/card";
  import { Badge } from "@/components/ui/badge";
  import { Button } from "@/components/ui/button";
  import { useWeightingsStore } from "@stores/weightings";

  const weightingsStore = useWeightingsStore();
  const { selectedWeighting, hasDuplicatedTargets, weightingLaunchEnabled, saveAttempted } = storeToRefs(weightingsStore);
  const { saveWeighting, launch, clear } = weightingsStore;

  const handleSave = () => {
    if (canSave.value) saveWeighting();
    else saveAttempted.value = true;
  };
  const canSave = computed(() => {
    if (!selectedWeighting.value) return false;
    if (!selectedWeighting.value.quotas) return false;
    if (hasDuplicatedTargets.value) return false;
    if (selectedWeighting.value.quotas.find((quota) => quota?.card_token === "" || quota?.quota_unit === "")) {
      return false;
    }
    let returned = true;
    selectedWeighting.value.quotas.forEach((quota) => {
      if (
        quota.quota_unit === "number" &&
        quota.answers?.reduce((acc, answer) => acc + answer.target, 0) != selectedWeighting.value.total
      ) {
        returned = false;
      } else if (quota.quota_unit === "percentage") {
        if (quota.answers?.reduce((acc, answer) => acc + answer.target, 0) !== 100) {
          returned = false;
        }
      }
    });
    return returned;
  });

  const defineWeightingLastLaunch = (launchedAt) => (launchedAt != null ? moment(launchedAt).format("LL") : "Unknown");
</script>

<template>
  <Card
    v-if="selectedWeighting !== null"
    class="sticky top-[88px] mb-4">
    <CardContent class="space-y-4 p-4">
      <h3 class="heading-title">Computed weights</h3>
      <div>
        <p
          v-if="!selectedWeighting?.average_weight"
          class="text-sm text-muted-foreground">
          No weights associated with this weighting yet.
          <span v-if="!weightingLaunchEnabled">Your survey must be Completed or Paused to compute weights.</span>
        </p>
        <div
          v-else
          class="flex flex-col gap-1 px-2">
          <div class="flex items-center gap-2">
            <span class="text-sm font-medium">Last update:</span>
            <span class="text-sm">{{ defineWeightingLastLaunch(selectedWeighting?.launched_at) }}</span>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-sm font-medium">Quality:</span>
            <span class="text-sm">{{ (selectedWeighting?.quality * 100).toFixed(2) || 0 }}%</span>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-sm font-medium">Average weight:</span>
            <span class="text-sm">{{ selectedWeighting?.average_weight.toFixed(2) }}</span>
          </div>
        </div>
      </div>
      <div
        v-if="(selectedWeighting?.saving && canSave) || !selectedWeighting?.saved"
        class="pt-2">
        <Button
          :variant="saveAttempted && !canSave ? 'destructive' : 'default'"
          :disabled="selectedWeighting?.saving"
          class="w-full"
          @click.exact="handleSave()">
          <Loader2
            v-if="selectedWeighting?.saving"
            class="h-4 w-4 animate-spin" />
          Save changes
        </Button>
      </div>
      <div
        v-else
        class="space-y-3 pt-2">
        <Badge
          v-if="selectedWeighting.launchHasError"
          variant="destructive">
          This weighting has an error, please retry
        </Badge>
        <div class="flex gap-2">
          <Button
            :variant="selectedWeighting?.launchHasError ? 'destructive' : 'default'"
            :disabled="selectedWeighting?.clearing || !weightingLaunchEnabled || selectedWeighting?.launched"
            class="flex-1"
            @click="launch()">
            <Loader2
              v-if="selectedWeighting?.launched"
              class="h-4 w-4 animate-spin" />
            Apply weighting
          </Button>
          <Button
            variant="outline"
            :disabled="!selectedWeighting?.launched_at || selectedWeighting?.launched || selectedWeighting?.clearing"
            @click="clear()">
            <Loader2
              v-if="selectedWeighting?.clearing"
              class="h-4 w-4 animate-spin" />
            Clear
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>
</template>
